import { useState } from 'react';
import { useGetPayerConnectionDetails } from '@frontend/api-insurance-detail';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';

const MinimumConnectionCountRequired = 3;

export const ConnectPayersBanner = () => {
  const { t } = useTranslation('insuranceDetails');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { navigate } = useSettingsNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();

  const locationId = selectedLocationIds[0];
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const {
    isLoading,
    isError,
    data: payerConnectionDetails,
  } = useGetPayerConnectionDetails(locationId, {
    enabled: isEnhancedInsuranceVerificationEnabled,
  });

  const shouldShowBanner = !isLoading && !isError && isEnhancedInsuranceVerificationEnabled;
  const payerConnectionCount = payerConnectionDetails?.connectionCount || 0;

  const showErrorBanner = shouldShowBanner && payerConnectionCount === 0;
  const showWarningBanner =
    shouldShowBanner && !!payerConnectionCount && payerConnectionCount < MinimumConnectionCountRequired;

  if (bannerDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setBannerDismissed(true);
  };

  if (showErrorBanner) {
    return (
      <GlobalBannerDynamic
        type='error'
        surfaceId='banner.wie-connect-payers'
        id='connect-payers-error-universal-banner'
        message={t('Insurance eligibility results may be incomplete or limited without payer credentials.')}
        title={t('Missing Payer Connections')}
        trackingId='connect-payers-button-universal-banner-error'
        action={{
          label: t('Connect Payers'),
          onClick: () => {
            navigate({ to: '/insurance/payer-connection-management' });
          },
        }}
        isDismissible={true}
        onDismiss={handleDismiss}
      />
    );
  } else if (showWarningBanner) {
    return (
      <GlobalBannerDynamic
        type='warning'
        surfaceId='banner.wie-connect-payers'
        id='connect-payers-warning-universal-banner'
        message={t('Add payer credentials to expand coverage and unlock more complete insurance eligibility results.')}
        title={t('Add Payer Connections')}
        trackingId='connect-payers-button-universal-banner-warning'
        action={{
          label: t('Connect Payers'),
          onClick: () => {
            navigate({ to: '/insurance/payer-connection-management' });
          },
        }}
        isDismissible={true}
        onDismiss={handleDismiss}
      />
    );
  }
  return null;
};
